import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePersistedState } from "@/lib/persist";
import { slaOverzichtNavOp } from "@/lib/overzichtNav";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Search, Building2 } from "lucide-react";
import { LaadBalk, TabelLaadRij } from "@/components/LaadIndicator";
import { toast } from "sonner";

const TYPES = ["alle", "klant", "leverancier", "beide"];
const STATS = ["alle", "suspect", "prospect", "klant", "inactief"];

const statusBadgeVariant = (s) =>
  s === "klant" ? "default" : s === "prospect" ? "secondary" : "outline";

function NewOrgDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ naam: "", type: "klant", status: "prospect", plaats: "", email: "", klantgroep: "" });
  const submit = async () => {
    if (!f.naam.trim()) { toast.error("Naam is verplicht"); return; }
    try {
      const { data } = await api.post("/organisaties", f);
      toast.success("Organisatie aangemaakt");
      setOpen(false); setF({ naam: "", type: "klant", status: "prospect", plaats: "", email: "", klantgroep: "" });
      onCreated(data);
    } catch { toast.error("Kon niet opslaan"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-org-btn"><Plus className="h-4 w-4 mr-2" />Nieuwe organisatie</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nieuwe organisatie</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Naam *</Label><Input data-testid="new-org-naam" value={f.naam} onChange={(e)=>setF({...f,naam:e.target.value})} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select value={f.type} onValueChange={(v)=>setF({...f,type:v})}>
                <SelectTrigger data-testid="new-org-type"><SelectValue /></SelectTrigger>
                <SelectContent>{["klant","leverancier","beide"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v)=>setF({...f,status:v})}>
                <SelectTrigger data-testid="new-org-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["suspect","prospect","klant","inactief"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Plaats</Label><Input value={f.plaats} onChange={(e)=>setF({...f,plaats:e.target.value})} /></div>
            <div><Label>Klantgroep</Label><Input value={f.klantgroep} onChange={(e)=>setF({...f,klantgroep:e.target.value})} /></div>
          </div>
          <div><Label>E-mail</Label><Input type="email" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="new-org-submit">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Organisaties() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [totaal, setTotaal] = useState(0);
  const [groepenLijst, setGroepenLijst] = useState([]);
  const [q, setQ] = usePersistedState("orgs:q", "");
  const [qDebounced, setQDebounced] = useState(q);
  const [type, setType] = usePersistedState("orgs:type", "alle");
  const [status, setStatus] = usePersistedState("orgs:status", "alle");
  const [klantgroep, setKlantgroep] = usePersistedState("orgs:klantgroep", "alle");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const perPage = 50;
  const navigate = useNavigate();
  // Deep-link via URL-parameters heeft voorrang op onthouden filters
  useEffect(() => {
    const t = params.get("type"); if (t) setType(t);
    const s = params.get("status"); if (s) setStatus(s);
    /* eslint-disable-next-line */
  }, []);
  const openOrg = (oid) => {
    slaOverzichtNavOp("organisaties", items.map(o => o.id), window.location.pathname + window.location.search);
    navigate(`/organisaties/${oid}`);
  };

  const load = async (p = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/organisaties", { params: { q: qDebounced, type, status, klantgroep, paged: true, page: p, page_size: perPage } });
      setItems(data.items || []); setTotaal(data.totaal || 0); setPage(data.page || p);
      setGroepenLijst(data.klantgroepen || []);
    } finally { setLoading(false); }
  };
  // Zoekterm debouncen
  useEffect(() => { const t = setTimeout(() => setQDebounced(q), 300); return () => clearTimeout(t); }, [q]);
  // Filters wijzigen → terug naar pagina 1
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [type, status, klantgroep, qDebounced]);
  useEffect(() => { load(page); /* eslint-disable-next-line */ }, [type, status, klantgroep, qDebounced, page]);

  const groepen = useMemo(() => ["alle", ...groepenLijst], [groepenLijst]);

  const paged = items;
  const totalPages = Math.max(1, Math.ceil(totaal / perPage));

  return (
    <div className="space-y-6" data-testid="orgs-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-muted-foreground">Relaties</div>
          <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Organisaties</h1>
        </div>
        <NewOrgDialog onCreated={(o) => setItems((x) => [o, ...x])} />
      </div>

      {loading && <LaadBalk label="Organisaties laden" />}

      <div className="flex gap-3 flex-wrap items-center bg-card border border-border rounded-xl p-3">
        <div className="relative flex-1 min-w-[200px] sm:min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Zoek op naam, plaats, KvK…" className="pl-9 h-11 text-base sm:h-10 sm:text-sm" value={q} onChange={(e)=>setQ(e.target.value)} data-testid="orgs-search" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-[110px] sm:w-[150px] h-11 sm:h-10" data-testid="orgs-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[110px] sm:w-[150px] h-11 sm:h-10" data-testid="orgs-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={klantgroep} onValueChange={setKlantgroep}>
          <SelectTrigger className="w-[130px] sm:w-[160px] h-11 sm:h-10" data-testid="orgs-groep-filter"><SelectValue placeholder="Klantgroep" /></SelectTrigger>
          <SelectContent>{groepen.map(g=><SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {/* Desktop tabel */}
        <table className="w-full hidden md:table">
          <thead className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left p-3">Klantcode</th>
              <th className="text-left p-3">Naam</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Plaats</th>
              <th className="text-left p-3">Klantgroep</th>
              <th className="text-left p-3">E-mail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paged.map((o) => (
              <tr key={o.id} onClick={() => openOrg(o.id)} className="table-row-hover" data-testid={`org-row-${o.id}`}>
                <td className="p-3 font-mono text-xs text-muted-foreground">{o.klantcode || "—"}</td>
                <td className="p-3 font-medium flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" />{o.naam}</td>
                <td className="p-3"><Badge variant="outline" className="capitalize">{o.type}</Badge></td>
                <td className="p-3">
                  <Badge variant={statusBadgeVariant(o.status)} className="capitalize">{o.status}</Badge>
                </td>
                <td className="p-3 text-muted-foreground">{o.plaats}</td>
                <td className="p-3 text-muted-foreground">{o.klantgroep}</td>
                <td className="p-3 text-muted-foreground">{o.email}</td>
              </tr>
            ))}
            {items.length === 0 && <TabelLaadRij colSpan={7} loading={loading} />}
          </tbody>
        </table>

        {/* Mobiel: kaartweergave met grote tap targets */}
        <div className="md:hidden divide-y divide-border" data-testid="orgs-mobiel">
          {paged.map((o) => (
            <button key={o.id} type="button" onClick={() => openOrg(o.id)}
              className="w-full text-left p-4 active:bg-secondary/60 hover:bg-secondary/40 transition-colors"
              data-testid={`org-card-${o.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="font-medium truncate">{o.naam}</span>
                </div>
                <Badge variant={statusBadgeVariant(o.status)} className="capitalize shrink-0 text-[10px]">{o.status}</Badge>
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                {o.klantcode && <Badge variant="outline" className="font-mono text-[10px]">{o.klantcode}</Badge>}
                <Badge variant="outline" className="capitalize text-[10px]">{o.type}</Badge>
                {o.plaats && <span>{o.plaats}</span>}
                {o.klantgroep && <span>· {o.klantgroep}</span>}
              </div>
              {o.email && <div className="text-xs text-muted-foreground mt-1 truncate">{o.email}</div>}
            </button>
          ))}
          {items.length === 0 && !loading && <div className="p-8 text-center text-muted-foreground text-sm">Geen resultaten</div>}
          {items.length === 0 && loading && <div className="p-8 text-center text-muted-foreground text-sm">Laden…</div>}
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-border text-sm" data-testid="orgs-paginatie">
            <span className="text-muted-foreground">{totaal} resultaten · pagina {page}/{totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page===1||loading} onClick={()=>setPage(p=>Math.max(1,p-1))} data-testid="orgs-vorige-pagina">Vorige</Button>
              <Button variant="outline" size="sm" disabled={page>=totalPages||loading} onClick={()=>setPage(p=>Math.min(totalPages,p+1))} data-testid="orgs-volgende-pagina">Volgende</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
